// src/blue-green/index.ts
var BLUE = "blue";
var GREEN = "green";
function normaliseColour(value, fallback) {
  const v = String(value ?? "").toLowerCase();
  if (v === BLUE) return BLUE;
  if (v === GREEN) return GREEN;
  if (v === "true" || v === "1") return BLUE;
  if (v === "false" || v === "0") return GREEN;
  return fallback;
}
function normaliseServices(value) {
  const out = {};
  if (value && typeof value === "object" && !Array.isArray(value)) {
    for (const [service, colour] of Object.entries(value)) {
      if (service) out[service] = normaliseColour(colour, GREEN);
    }
  }
  return out;
}
var BlueRoutingConfig = class _BlueRoutingConfig {
  default;
  services;
  constructor(deflt = GREEN, services = {}) {
    this.default = deflt;
    this.services = { ...services };
  }
  /** Resolve the effective colour for `service` (override else default). */
  colourFor(service) {
    return this.services[service] ?? this.default;
  }
  /** True when `service` resolves to the blue (latest) deploy. */
  isBlueFor(service) {
    return this.colourFor(service) === BLUE;
  }
  /** True when every service resolves to blue (no green overrides). */
  isGlobalBlue() {
    return this.default === BLUE && !Object.values(this.services).includes(GREEN);
  }
  /** True when nothing routes to blue (the normal, non-QA state). */
  isAllGreen() {
    return this.default === GREEN && !Object.values(this.services).includes(BLUE);
  }
  /** Service names explicitly pinned to a given colour. */
  servicesWithColour(colour) {
    return Object.keys(this.services).filter((s) => this.services[s] === colour);
  }
  /** Plain-object form for persistence / the JWT claim / equality checks. */
  toJSON() {
    return { default: this.default, services: { ...this.services } };
  }
  /** A fresh "everything green" config (the normal, non-QA state). */
  static allGreen() {
    return new _BlueRoutingConfig(GREEN, {});
  }
  /** A fresh "everything blue" config (global blue). */
  static globalBlue() {
    return new _BlueRoutingConfig(BLUE, {});
  }
  /**
   * Coerce a stored/claimed value into the structured shape.
   *
   * Tolerant of the LEGACY flat service-whitelist (`string[]`, where an empty
   * list meant "everything blue") so a pre-prod cutover and any in-flight tokens
   * keep working. Matches the Python `BlueRoutingConfig.from_value`:
   *
   *   - `null` / `{}` / `[]`               → `default=blue` (legacy empty = all blue)
   *   - `["authentication", "permissions"]`→ `default=green` + those services blue
   *   - `{ default, services }`            → structured config
   *   - bare `{ service: colour }` map     → `default=green` + that map
   */
  static fromValue(value) {
    if (value === null || value === void 0) return _BlueRoutingConfig.globalBlue();
    if (value instanceof _BlueRoutingConfig) return value;
    if (Array.isArray(value)) {
      const services = {};
      for (const item of value) if (item) services[String(item)] = BLUE;
      if (Object.keys(services).length === 0) return _BlueRoutingConfig.globalBlue();
      return new _BlueRoutingConfig(GREEN, services);
    }
    if (typeof value === "object") {
      const obj = value;
      if (Object.keys(obj).length === 0) return _BlueRoutingConfig.globalBlue();
      if ("default" in obj || "services" in obj) {
        return new _BlueRoutingConfig(
          normaliseColour(obj.default, GREEN),
          normaliseServices(obj.services)
        );
      }
      return new _BlueRoutingConfig(GREEN, normaliseServices(obj));
    }
    return _BlueRoutingConfig.allGreen();
  }
};
function routingFromQaConfig(qa) {
  if (!qa) return BlueRoutingConfig.allGreen();
  const cfg = qa.config;
  if (cfg && typeof cfg === "object" && !Array.isArray(cfg)) {
    return BlueRoutingConfig.fromValue(cfg);
  }
  if (qa.environment !== BLUE) return BlueRoutingConfig.allGreen();
  return BlueRoutingConfig.fromValue(cfg ?? []);
}

// src/flags/index.ts
var DEFAULT_OVERRIDE_KEY = "authe:flag-overrides";
var FeatureFlags = class {
  defaults;
  claimsMap;
  policyMap;
  variantDefaultsMap;
  variantClaimsMap;
  routing;
  overrideKey;
  getStorage;
  listeners = /* @__PURE__ */ new Set();
  rev = 0;
  constructor(init = {}) {
    this.defaults = init.defaults ?? {};
    this.claimsMap = init.claims ?? {};
    this.policyMap = init.policy ?? {};
    this.variantDefaultsMap = init.variantDefaults ?? {};
    this.variantClaimsMap = init.variantClaims ?? {};
    this.routing = routingFromQaConfig(init.qaConfig ?? null);
    this.overrideKey = init.overrideStorageKey ?? DEFAULT_OVERRIDE_KEY;
    this.getStorage = init.getStorage ?? (() => typeof localStorage !== "undefined" ? localStorage : void 0);
  }
  /** Every stored override, boolean and variant alike (one JSON object). */
  rawOverrides() {
    try {
      const parsed = JSON.parse(this.getStorage()?.getItem(this.overrideKey) ?? "{}");
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  overrides() {
    return Object.fromEntries(
      Object.entries(this.rawOverrides()).filter((e) => typeof e[1] === "boolean")
    );
  }
  variantOverrides() {
    return Object.fromEntries(
      Object.entries(this.rawOverrides()).filter((e) => typeof e[1] === "string")
    );
  }
  /** Write one override key (a boolean, a variant string, or `undefined` to clear) and notify. */
  writeOverride(name, value) {
    const storage = this.getStorage();
    if (!storage) return;
    const overrides = this.rawOverrides();
    if (overrides[name] === value) return;
    if (value === void 0) delete overrides[name];
    else overrides[name] = value;
    storage.setItem(this.overrideKey, JSON.stringify(overrides));
    this.rev += 1;
    for (const listener of this.listeners) listener();
  }
  /** Effective flag value: override > policy > claim > default > false. */
  isEnabled(name) {
    return this.valueOf(name) ?? false;
  }
  /**
   * TRI-STATE read: the effective value, or `undefined` when no layer holds an
   * opinion about `name`.
   *
   * The distinction matters wherever a consumer has a non-false default of its
   * own — the transport registry is the case in point: without this it cannot
   * tell "the user chose REST" from "nobody has said anything", so an explicit
   * `false` falls through to a `graphql` default and the choice is ignored.
   */
  valueOf(name) {
    const fromOverride = this.overrides()[name];
    if (fromOverride !== void 0) return fromOverride;
    const fromPolicy = this.policyMap[name];
    if (fromPolicy !== void 0) return fromPolicy;
    const fromClaim = this.claimsMap[name];
    if (fromClaim !== void 0) return fromClaim;
    return this.defaults[name];
  }
  /** Which layer answered for `name`, or `null` when none did. */
  layerOf(name) {
    if (this.overrides()[name] !== void 0) return "override";
    if (this.policyMap[name] !== void 0) return "policy";
    if (this.claimsMap[name] !== void 0) return "claim";
    if (this.defaults[name] !== void 0) return "default";
    return null;
  }
  /**
   * Replace the centrally managed layer wholesale and notify subscribers.
   *
   * Wholesale rather than per-key because this layer is a RESOLVED document: a
   * key that disappears from it (a workspace default an admin cleared) has to
   * stop applying, and a merge would keep it forever.
   *
   * No-ops when the map is unchanged, so the loader that owns it can push on
   * every hydration without re-rendering the tree.
   */
  setPolicy(map) {
    if (sameFlagMap(this.policyMap, map)) return;
    this.policyMap = { ...map };
    this.rev += 1;
    for (const listener of this.listeners) listener();
  }
  /** The current policy layer (for a UI that reports where a value came from). */
  policy() {
    return { ...this.policyMap };
  }
  /**
   * Replace the claim layer wholesale and notify subscribers — the per-identity
   * platform flags (customer feature flags resolved for the signed-in tenancy).
   *
   * Wholesale for the same reason as {@link setPolicy}: the layer is a resolved
   * document, so a flag an operator stopped setting must stop applying. No-ops
   * when unchanged, so the loader can push on every render of its data.
   */
  setClaims(map) {
    if (sameFlagMap(this.claimsMap, map)) return;
    this.claimsMap = { ...map };
    this.rev += 1;
    for (const listener of this.listeners) listener();
  }
  /** The current claim layer (for a UI that reports where a value came from). */
  claims() {
    return { ...this.claimsMap };
  }
  /** Set a local override (persists in storage). Pass `null` to clear it. */
  setOverride(name, value) {
    this.writeOverride(name, value ?? void 0);
  }
  // ---- Variants (multi-valued flags) -------------------------------------
  /**
   * A variant flag's effective value: override > claim > default, or
   * `undefined` when no layer names one. The caller validates the value against
   * its own option list — an override written for an older option set must not
   * be trusted blindly.
   */
  variantOf(name) {
    return this.variantOverrides()[name] ?? this.variantClaimsMap[name] ?? this.variantDefaultsMap[name];
  }
  /** Which layer answered for a variant flag, or `null` when none did. */
  variantLayerOf(name) {
    if (this.variantOverrides()[name] !== void 0) return "override";
    if (this.variantClaimsMap[name] !== void 0) return "claim";
    if (this.variantDefaultsMap[name] !== void 0) return "default";
    return null;
  }
  /** Force a variant locally (persists in storage, e.g. from the debug panel). `null` clears it. */
  setVariantOverride(name, value) {
    this.writeOverride(name, value ?? void 0);
  }
  /**
   * Observe override writes.
   *
   * Flag values live in storage and this object's identity never changes, so a
   * React tree has nothing to re-render on when a setting is toggled: the new
   * value is there, but no one is asked to look again. That is fine for a read
   * issued fresh on every call (a react-query `queryFn` re-run by an
   * invalidation) and wrong for anything that resolves its transport during
   * render — it keeps using the old one until something unrelated re-renders it.
   *
   * {@link setOverride}, {@link setPolicy} and {@link setClaims} notify. Defaults
   * are fixed for the lifetime of the instance, so there is nothing else to announce.
   *
   * @returns an unsubscribe function.
   */
  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  /** Snapshot that changes on every override write (for `useSyncExternalStore`). */
  getRevision = () => this.rev;
  all() {
    return { ...this.defaults, ...this.claimsMap, ...this.policyMap, ...this.overrides() };
  }
  // ---- QA mode -----------------------------------------------------------
  /** The structured blue/green routing config resolved from the `qa_config` claim. */
  routingConfig() {
    return this.routing;
  }
  /**
   * Coarse QA environment label: `"blue"` when ANY service routes to the latest
   * deploy, else `"green"`. Prefer {@link colourFor}/{@link isBlueFor} for
   * per-service decisions.
   */
  qaEnvironment() {
    return this.routing.isAllGreen() ? "green" : "blue";
  }
  /** True when blue routing is active for at least one service. */
  isBlue() {
    return !this.routing.isAllGreen();
  }
  /** The resolved deployment colour for a given service (override else default). */
  colourFor(service) {
    return this.routing.colourFor(service);
  }
  /** Whether blue routing applies to a given service. */
  isBlueFor(service) {
    return this.routing.isBlueFor(service);
  }
  /** Service names explicitly pinned to blue (empty when the default is blue). */
  blueServices() {
    return this.routing.servicesWithColour("blue");
  }
};
function sameFlagMap(a, b) {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every((k) => a[k] === b[k]);
}

export { FeatureFlags };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map